/**
 * The main js file for the theme
 *
 * This is the entry point where all modules are imported and compiled.
 *
 * @package
 * @since   1.0.1
 */

import FontFaceObserver from 'fontfaceobserver';
import { Breakpoints } from './theme/breakpoints';
import { scrollToHash } from './theme/helpers';
import {
	imageLinkQuerySelector,
	videoLinkQuerySelector,
	youtubeRegex,
	youtubePageRegex,
} from './theme/lightboxes/helpers';

class Embark {
	/**
	 * Array of font-family names to observe with FontFaceObserver to ensure they load.
	 */
	fonts = ['Roboto'];

	/**
	 * Common elements used throughout the project.
	 */
	els = {
		html: document.documentElement,
		body: document.body,
		header: document.getElementById('js-site-header'),
		headerSearch: document.querySelector('.site-header .search-form'),
		navContainer: document.querySelector('.site-header__navigation'),
		mainMenu: document.querySelector('.main-menu'),
		navicon: document.getElementById('js-navicon'),
	};

	/**
	 * Each object in this array has the following parameters:
	 *
	 * path: The filepath, without file extension, to import relative to "./theme/".
	 * test: A test to check if the module should be imported or not. Typically,
	 * this is simply checking to see if an element exists in the DOM.
	 */
	imports = [
		{
			path: 'animations',
			test: document.querySelector('[class*="em-animate--"]'),
		},
		{
			path: 'skip-to-content',
			test: true,
		},
		{
			path: 'sticky-header',
			test: this.els.header,
		},
		{
			path: 'main-menu',
			test: this.els.mainMenu,
			delay: false,
		},
		{
			path: 'click-reveal-search',
			test: this.els.headerSearch,
		},
		{
			path: 'forms/file-input',
			test: document.querySelector('input[type="file"]'),
			delay: false,
		},
		{
			path: 'forms/ios-select-fix',
			test: document.querySelector('.ios select'),
		},
		{
			path: 'forms/gf-tabindex-fix',
			test: document.querySelector(
				'.ginput_container_consent [type="checkbox"][tabindex], .gform_button[tabindex]',
			),
		},
		{
			path: 'forms/gf-validation',
			test: document.querySelector('.gform_validation_error'),
			delay: false,
		},
		{
			path: 'comment-reply',
			test: document.querySelector('#reply-title'),
		},
		{
			path: 'listbox',
			test: document.querySelector('.listbox'),
		},
		{
			path: 'popups',
			test: true,
			delay: false,
		},
		{
			path: 'scroll-to-top',
			test: document.querySelector('.scroll-to-top'),
		},
		{
			path: 'slider',
			test: document.querySelector('.splide[data-splide]'),
		},
		{
			path: 'pdf-links',
			test: true,
		},
		{
			path: 'lightboxes',
			test: (() => {
				const els = [
					...document.querySelectorAll(
						`${imageLinkQuerySelector}, ${videoLinkQuerySelector}, .wp-block-embark-filterable-content`,
					),
				];
				if (els.length) {
					const filtered = els.filter((el) => {
						// Don't initialize lightboxes for YouTube profile or channel links.
						if (
							el.nodeName === 'A'
							&& el.href.match(youtubeRegex)
							&& el.href.match(youtubePageRegex)
						) {
							return false;
						}

						return true;
					});

					return filtered.length > 0;
				}

				return false;
			})(),
		},
	];

	/**
	 * Breakpoints used throughout the project. These should match up with
	 * the breakpoints defined in src/scss/_config.scss.
	 */
	breakpoints = new Breakpoints([
		{
			key: 'xs',
			value: 480,
		},
		{
			key: 'sm',
			value: 640,
		},
		{
			key: 'md',
			value: 850,
		},
		{
			key: 'lg',
			value: 1120,
		},
		{
			key: 'xl',
			value: 1380,
		},
		{
			key: 'menu',
			value: 1000,
		},
	]);

	// Object to store components in order to expose them to other components.
	components = {};

	// Array of events that should trigger delayed imports.
	interactionEvents = ['click', 'mousedown', 'mousemove', 'keydown', 'touchstart', 'keydown', 'scroll', 'pointerdown'];

	// Flag to prevent multiple imports of delayed modules.
	isImportingDelayedJS = false;

	// Object to store the focus trap element and callback.
	focusTrap = {
		el: null,
		callback: null,
	};

	/**
	 * Set variables for common elements, fonts, breakpoints and dynamically
	 * import all modules for the project.
	 */
	constructor() {
		this.loadFonts();
		this.loadJS();
		this.makeImagesAccessible();
	}

	/**
	 * Watch as fonts are loaded and add a class to the html element when they are.
	 * Then set a cookie so that on subsequent page views, head.js can quickly add
	 * the class, avoiding FOUT.
	 */
	loadFonts = () => {
		if (!this.els.html.classList.contains('fonts-loaded')) {
			Promise.all(
				this.fonts.map((font) => new FontFaceObserver(font).load()),
			).then(() => {
				this.els.html.classList.add('fonts-loaded');
				document.cookie = 'emFontsLoaded=true; SameSite=None; Secure';
			});
		}
	};

	/**
	 * Load a module by path.
	 *
	 * @param {string} path The path to the module.
	 */
	loadModule = (path) => {
		import(
			/* webpackChunkName: "[request]" */
			`./theme/${path}`
		).catch((error) => {
			console.error(`Error loading: ${path}`, error);
		});
	};

	/**
	 * Load modules that are needed on the current page.
	 */
	loadJS = () => {
		this.imports.forEach(({ path, test, delay }) => {
			if (delay === false && test) {
				this.loadModule(path);
			}
		});

		// Load delayed JS after user interaction.
		this.interactionEvents.forEach((event) => {
			document.addEventListener(event, this.loadDelayedJS, { once: true });
		});
	};

	/**
	 * Load delayed JS after user interaction.
	 */
	loadDelayedJS = () => {
		if (this.isImportingDelayedJS) {
			return;
		}

		// Prevent multiple imports of delayed modules.
		this.isImportingDelayedJS = true;

		// Import modules that are delayed.
		this.imports.forEach(({ path, test, delay }) => {
			if (delay !== false && test) {
				this.loadModule(path);
			}
		});

		// Remove event listeners.
		this.interactionEvents.forEach((event) => {
			document.removeEventListener(event, this.loadDelayedJS);
		});
	};

	/**
	 * Add empty alt attributes to images that don't have them and set aria-hidden to true.
	 */
	makeImagesAccessible = () => {
		const noAltImages = [
			...document.querySelectorAll('img:not([alt]), img[alt=""]'),
		];
		if (noAltImages.length) {
			noAltImages.forEach((img) => {
				img.setAttribute('alt', '');
				img.setAttribute('aria-hidden', 'true');
			});
		}
	};
}

window.addEventListener('load', scrollToHash);
window.addEventListener('hashchange', scrollToHash);
document.addEventListener('DOMContentLoaded', () => {
	window.embark = new Embark();

	// Trigger custom event after the Embark object is created.
	const event = new CustomEvent('embarkReady', {
		detail: { embark: window.embark },
	});
	document.dispatchEvent(event);
});
